import './index.css';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useCallback, useState, useRef, useLayoutEffect, useEffect } from 'react';
import { CSSTransition } from 'react-transition-group';
import classnames from 'classnames';
import { useDebounce } from '@hh.ru/magritte-common-func-utils';
import { useEscapeToClose } from '@hh.ru/magritte-common-keyboard';
import { useResize } from '@hh.ru/magritte-common-resize';
import { useMultipleRefs } from '@hh.ru/magritte-common-use-multiple-refs';
import { useSwipeHandlers } from '@hh.ru/magritte-common-use-swipe';
import { Action } from '@hh.ru/magritte-ui-action';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui-icon/icon';
import { Layer } from '@hh.ru/magritte-ui-layer';
import { Link } from '@hh.ru/magritte-ui-link';
import { SnackbarTransitionName, SnackbarLayout } from './types.js';
import { useAriaSnackbar } from './useAriaSnackbar.js';
import { useTimer, Timer } from '@hh.ru/magritte-ui-timer';
import { s as styles } from './snackbar-CR_b_R8I.js';

const CLASS_NAMES = {
    [SnackbarTransitionName.Show]: {
        appear: styles.appear,
        appearActive: styles['appear-active'],
        appearDone: styles['appear-done'],
    },
    [SnackbarTransitionName.Hide]: {
        exit: styles.exit,
        exitActive: styles['exit-active'],
        exitDone: styles['exit-done'],
    },
    [SnackbarTransitionName.Autohide]: {
        exit: styles.exit,
        exitActive: styles['exit-active_autohide'],
        exitDone: styles['exit-done'],
    },
    [SnackbarTransitionName.Replace]: {
        appear: styles.appear,
        appearDone: styles['appear-done'],
    },
    [SnackbarTransitionName.RemoveImmediately]: {},
};
const DEFAULT_AUTOHIDE_TIME_MS = 6000;
const SnackbarRenderer = ({ actionLabel, addon, align = 'bottom', animateOut, autohideTime = DEFAULT_AUTOHIDE_TIME_MS, children, containerId, isShown, onAction, onClose, onShown, showClose, showTimer, timeout, transition, 'aria-label': ariaLabel, 'aria-label-close': ariaLabelClose, role = 'status', ...props }) => {
    const actionFunc = useCallback(() => onAction?.(), [onAction]);
    const [hasFocus, setHasFocus] = useState(false);
    const [hasHover, setHasHover] = useState(false);
    const hasTouchSupportRef = useRef(false);
    const [layout, setLayout] = useState(SnackbarLayout.Horizontal);
    const autohideFunc = useCallback(() => animateOut(SnackbarTransitionName.Autohide), [animateOut]);
    const closeFunc = useCallback(() => animateOut(SnackbarTransitionName.Hide), [animateOut]);
    const previousMetrics = useRef(null);
    const ariaAttributes = useAriaSnackbar({ role, hasAction: !!actionLabel, 'aria-label': ariaLabel });
    const textRef = useRef(null);
    const resizableContentRef = useRef(null);
    const visible = transition === SnackbarTransitionName.Show || transition === SnackbarTransitionName.Replace;
    const maxDistanceSqRef = useRef({ nearestSide: Number.POSITIVE_INFINITY, farSide: Number.POSITIVE_INFINITY });
    const swipeThresholdXRef = useRef(null);
    const swipeThresholdYRef = useRef(null);
    const removeImmediately = useCallback(() => {
        animateOut(SnackbarTransitionName.RemoveImmediately);
        onClose?.();
    }, [animateOut, onClose]);
    const snackbarRef = useRef(null);
    const bindEscapeToClose = useEscapeToClose(closeFunc);
    const snackbarRefMultiplexer = useMultipleRefs(snackbarRef, bindEscapeToClose);
    const calculateLayout = useCallback(() => {
        if (textRef.current && resizableContentRef.current) {
            const metrics = resizableContentRef.current?.getBoundingClientRect();
            const style = window.getComputedStyle(textRef.current);
            const isVerticalLayout = textRef.current.getBoundingClientRect().height - parseInt(style.lineHeight, 10) > 1;
            if (previousMetrics.current !== null) {
                const increaseScreenSize = metrics.width > previousMetrics.current.width;
                setLayout((layout) => {
                    /* Уменьшаем размер экрана и если нужно переключиться в вертикальный лейаут */
                    if (!increaseScreenSize && isVerticalLayout && layout === SnackbarLayout.Horizontal) {
                        return SnackbarLayout.Vertical;
                    }
                    /* Увеличиваем размер экрана и если нужно переключиться в вертикальный лейаут */
                    if (increaseScreenSize && isVerticalLayout && layout === SnackbarLayout.Horizontal) {
                        return SnackbarLayout.Vertical;
                    }
                    /* Кейсы когда уменьшаем или увеличиваем размер экрана и нужно переключиться
                     * с вертикального на горизонтальный лейаут скипаем, потому как доступного места для текста
                     * меньше и может оказаться что текст будет  в 2 строки и надо
                     * снова переключаться на вертикальный лейаут. */
                    return layout;
                });
            }
            else {
                setLayout(isVerticalLayout ? SnackbarLayout.Vertical : SnackbarLayout.Horizontal);
            }
            previousMetrics.current = resizableContentRef.current?.getBoundingClientRect();
        }
    }, []);
    useLayoutEffect(calculateLayout, [calculateLayout]);
    useResize(useDebounce(calculateLayout, 64));
    useTimer(!!autohideTime && !showTimer ? autohideTime : -1, (callable) => {
        const timeoutId = setTimeout(callable, 50);
        return () => clearTimeout(timeoutId);
    }, (remaining) => {
        remaining < 0 && autohideFunc();
    }, hasFocus || hasHover);
    useEffect(() => void (!!actionLabel && snackbarRef.current?.focus?.()), [actionLabel]);
    useEffect(() => {
        if (snackbarRef.current !== null) {
            const maxDistanceX = snackbarRef.current.clientWidth * 0.66;
            const maxDistanceNearestY = snackbarRef.current.clientHeight * 0.5;
            const maxDistanceFarY = snackbarRef.current.clientHeight;
            maxDistanceSqRef.current.nearestSide = Math.pow(maxDistanceX, 2) + Math.pow(maxDistanceNearestY, 2);
            maxDistanceSqRef.current.farSide = Math.pow(maxDistanceX, 2) + Math.pow(maxDistanceFarY, 2);
            swipeThresholdXRef.current = { minDelta: -maxDistanceX, maxDelta: maxDistanceX };
            swipeThresholdYRef.current =
                align === 'top'
                    ? { minDelta: -maxDistanceNearestY, maxDelta: maxDistanceFarY }
                    : { minDelta: -maxDistanceFarY, maxDelta: maxDistanceNearestY };
        }
        // при смене layout может поменяться clientHeight
    }, [align, visible, layout]);
    const handleSwipeMove = useCallback((event) => {
        if (snackbarRef.current !== null) {
            // насколько прозрачным нужно сделать снекбар с учетом расстояния от начальной позиции
            let maxDistanceSq = maxDistanceSqRef.current.nearestSide;
            // определяем был ли свайп в дальнюю сторону экрана по вертикали
            if ((align === 'top' && event.distanceY > 0) || (align === 'bottom' && event.distanceY < 0)) {
                maxDistanceSq = maxDistanceSqRef.current.farSide;
            }
            const fadeDistanceSq = Math.pow(event.distanceX, 2) + Math.pow(event.distanceY, 2);
            snackbarRef.current.style.opacity = `${1 - fadeDistanceSq / maxDistanceSq}`;
            snackbarRef.current.style.transform = `translate(${event.distanceX}px, ${event.distanceY}px)`;
        }
    }, [align]);
    const handleSwipeEnd = useCallback(() => {
        if (snackbarRef.current !== null) {
            snackbarRef.current.style.opacity = `0`;
        }
        closeFunc();
    }, [closeFunc]);
    const handleSwipeCancel = useCallback(() => {
        if (snackbarRef.current !== null) {
            snackbarRef.current.style.opacity = `1`;
            snackbarRef.current.style.transform = ``;
        }
    }, []);
    const swipeHandlers = useSwipeHandlers({
        thresholdXRef: swipeThresholdXRef,
        thresholdYRef: swipeThresholdYRef,
        onSwipeMove: handleSwipeMove,
        onSwipeEnd: handleSwipeEnd,
        onSwipeCancel: handleSwipeCancel,
    });
    useEffect(() => {
        const snackbar = snackbarRef.current;
        if (!snackbar) {
            return void 0;
        }
        const handleTouchStart = (event) => {
            event.stopPropagation();
            hasTouchSupportRef.current = true;
            setHasHover(true);
            swipeHandlers.onTouchStart(event);
        };
        const handleTouchMove = (event) => {
            event.preventDefault();
            event.stopPropagation();
            swipeHandlers.onTouchMove(event);
        };
        const handleTouchEnd = (event) => {
            setHasHover(false);
            swipeHandlers.onTouchEnd(event);
        };
        const handleTouchCancel = (event) => {
            setHasHover(false);
            swipeHandlers.onTouchCancel(event);
        };
        snackbar.addEventListener('touchstart', handleTouchStart);
        snackbar.addEventListener('touchmove', handleTouchMove, { passive: false });
        snackbar.addEventListener('touchend', handleTouchEnd);
        snackbar.addEventListener('touchcancel', handleTouchCancel);
        return () => {
            snackbar.removeEventListener('touchstart', handleTouchStart);
            snackbar.removeEventListener('touchmove', handleTouchMove);
            snackbar.removeEventListener('touchend', handleTouchEnd);
            snackbar.removeEventListener('touchcancel', handleTouchCancel);
        };
    }, [swipeHandlers]);
    return (jsx(CSSTransition, { appear: true, classNames: CLASS_NAMES[transition], in: visible, nodeRef: snackbarRef, onEntered: onShown, onExited: removeImmediately, timeout: timeout, children: jsx(Layer, { layer: "snackbar", children: jsx("div", { ...props, ...ariaAttributes, className: classnames(styles.snackbar, {
                    [styles[`snackbar_align-${align}`]]: align,
                    [styles.snackbarInBody]: !containerId,
                    [styles.snackbarLayoutVertical]: layout === SnackbarLayout.Vertical,
                    [styles.snackbarLayoutHorizontal]: layout === SnackbarLayout.Horizontal,
                }), ref: snackbarRefMultiplexer, onFocus: (event) => {
                    if (event.target !== snackbarRef.current && !hasTouchSupportRef.current) {
                        setHasFocus(true);
                    }
                }, onBlur: () => !hasTouchSupportRef.current && setHasFocus(false), onMouseEnter: () => setHasHover(true), onMouseLeave: () => setHasHover(false), children: jsxs("div", { className: styles.snackbarContent, children: [!!addon && (jsx("div", { className: styles.snackbarAddon, "data-qa": "snackbar-addon", children: addon })), !!autohideTime && showTimer && (jsx("div", { className: styles.snackbarAddon, "data-qa": "snackbar-timer", children: jsx(Timer, { timeout: autohideTime, pause: hasFocus || hasHover, callback: autohideFunc }) })), jsxs("div", { className: styles.snackbarTextAndAction, ref: resizableContentRef, children: [jsx("div", { className: styles.snackbarText, ref: textRef, id: "dialog-description", children: children }), !!actionLabel && (jsx(Link, { "data-qa": "snackbar-user-action", "aria-label": actionLabel, Element: "button", type: "button", onClick: actionFunc, typography: "subtitle-1-semibold", style: "contrast", children: actionLabel }))] }), showClose && (jsx("div", { className: styles.snackbarClose, children: jsx(Action, { "data-qa": "snackbar-close-action", tabIndex: actionLabel ? 0 : -1, "aria-label": actionLabel && typeof ariaLabelClose === 'string' ? ariaLabelClose : undefined, icon: CrossOutlinedSize24, mode: "secondary", onClick: closeFunc, style: "contrast" }) }))] }) }) }) }));
};

export { SnackbarRenderer };
//# sourceMappingURL=SnackbarRenderer.js.map
